import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import LineChart, { LineChartSeries } from '../../Charts/LineChart'
import { formatDate } from '../../../lib/Dates'
import { useFormatCurrency } from '../../../lib/CurrencyFormatter'
import { Budget, BudgetAmount } from '../../../lib/Types'
import { useBudgetSums } from '../lib/Hooks/Budget'

interface Props {
  budget: Budget
  showEmptyWarning?: boolean
}

// Reuses the existing app palette rather than introducing new colors.
// Red/green are the same trial-ended/active-trial state colors already
// used on this card by TrialLabel (text-red-600 / text-green-700), just
// the saturated text variant instead of the pale card-background tint.
// Blue matches the line color used in the accounting development chart
// (theme-blue, LineChart's default line color).
const EXPENSE_COLOR = '#dc2626'
const INCOME_COLOR = '#457B9D'
const SAVING_COLOR = '#15803d'

const MAX_POINTS = 10

// Expense/income/saving sums are each their own history, recorded
// independently whenever a spend of that type changes, so they don't
// share timestamps. To plot them as three aligned lines, we bucket all
// of them onto a shared per-day timeline, and for each line carry
// forward its most recent known record into every day on that timeline
// (it stays true until a newer record replaces it) - so several sums
// landing on the same day just share that day's x-axis point.
const entryAt = (
  amounts: BudgetAmount[],
  timestamp: number,
): BudgetAmount | undefined =>
  amounts
    .filter((amount) => new Date(amount.created_date).getTime() <= timestamp)
    .at(-1)

// The API returns a zero-amount record rather than an empty array for a
// type that's never had a spend, so an empty-array check isn't enough -
// a line only earns its place on the chart once it's actually had a
// non-zero amount at some point.
const hasHistory = (amounts: BudgetAmount[]): boolean =>
  amounts.some((amount) => amount.amount !== 0)

// yyyy-mm-dd sorts chronologically as a plain string, and is a stable
// key to bucket same-day timestamps under.
const dayKeyOf = (timestamp: number): string => formatDate(new Date(timestamp), 'yyyy-mm-dd')

const endOfDay = (dayKey: string): number => {
  const [year, month, day] = dayKey.split('-').map(Number)
  return new Date(year, month - 1, day, 23, 59, 59, 999).getTime()
}

const formatDayLabel = (dayKey: string): string => {
  const [year, month, day] = dayKey.split('-')
  return `${day}/${month}-${year}`
}

const buildTimeline = (expense: BudgetAmount[], income: BudgetAmount[], saving: BudgetAmount[]) => {
  const timestamps = [...expense, ...income, ...saving].map((amount) =>
    new Date(amount.created_date).getTime(),
  )
  const uniqueDays = Array.from(new Set(timestamps.map(dayKeyOf))).sort()
  const daysToRender = uniqueDays.slice(-MAX_POINTS)
  const dayEndTimestamps = daysToRender.map(endOfDay)

  const entriesFor = (amounts: BudgetAmount[]) =>
    dayEndTimestamps.map((timestamp) => entryAt(amounts, timestamp))

  return {
    labels: daysToRender.map(formatDayLabel),
    expense: entriesFor(expense),
    income: entriesFor(income),
    saving: entriesFor(saving),
  }
}

const BudgetDevelopmentChart: FC<Props> = ({ budget, showEmptyWarning = true }) => {
  const { t } = useTranslation(['budget'])
  const formatCurrency = useFormatCurrency()
  const { data: budgetSums } = useBudgetSums(budget)

  if (!budgetSums) {
    return null
  }

  const hasExpense = hasHistory(budgetSums.expense)
  const hasIncome = hasHistory(budgetSums.income)
  const hasSaving = hasHistory(budgetSums.saving)
  const hasData = hasExpense || hasIncome || hasSaving

  const timeline = buildTimeline(budgetSums.expense, budgetSums.income, budgetSums.saving)

  // Lists the spends behind a hovered point, same as the budget page's
  // own development chart used to - a bare total isn't enough to tell
  // what actually caused the change.
  const buildTooltip = (entries: (BudgetAmount | undefined)[]) => (dataIndex: number): string[] => {
    const entry = entries[Math.min(dataIndex, entries.length - 1)]

    if (!entry || entry.spends.length === 0) {
      return [`${t('budget:shared.total')}: ${formatCurrency(entry?.amount ?? 0)}`]
    }

    const lines = entry.spends.map(
      (spend) => `${spend.title}: ${formatCurrency(spend.amount)}`,
    )
    lines.push('')

    const total = entry.spends.reduce((sum, spend) => sum + spend.amount, 0)
    const sign = total > 0 ? '+' : ''
    lines.push(
      `${t('budget:developmentChart.totalAdjustment')}: ${sign}${formatCurrency(total)}`,
    )

    return lines
  }

  return (
    <>
      {!hasData && showEmptyWarning && (
        <p className='italic'>{t('budget:shared.notEnoughData')}</p>
      )}
      {hasData && (
        <LineChart
          labels={timeline.labels}
          showLegend={false}
          series={[
            hasExpense && {
              label: t('budget:developmentChart.expense'),
              color: EXPENSE_COLOR,
              data: timeline.expense.map((entry) => entry?.amount ?? 0),
              tooltipCallback: buildTooltip(timeline.expense),
            },
            hasIncome && {
              label: t('budget:developmentChart.income'),
              color: INCOME_COLOR,
              data: timeline.income.map((entry) => entry?.amount ?? 0),
              tooltipCallback: buildTooltip(timeline.income),
            },
            hasSaving && {
              label: t('budget:developmentChart.saving'),
              color: SAVING_COLOR,
              data: timeline.saving.map((entry) => entry?.amount ?? 0),
              tooltipCallback: buildTooltip(timeline.saving),
            },
          ].filter(Boolean) as LineChartSeries[]}
        />
      )}
    </>
  )
}

export default BudgetDevelopmentChart
